import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { LoadingGif } from "../../Utils/Loader";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { Container } from "../../Utils/styledComponents";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStethoscope,
  faSearch,
  faFileExcel,
  faFilePdf,
  faDollarSign,
  faCoins,
  faUserMd,
  faArrowLeft,
  faList,
  faHospital,
  faCalendarAlt,
  faCreditCard,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../actions/snackbarActions";


const BackButton = ({ to, text }) => (
  <Link to={to} className="text-decoration-none">
    <button 
      className="btn btn-outline-dark d-flex align-items-center gap-2 mb-4"
      style={{
        padding: "8px 16px",
        fontSize: "0.95rem",
        minWidth: "auto",
        height: "42px"
      }}
    >
      <FontAwesomeIcon icon={faArrowLeft} />
      {text}
    </button>
  </Link>
);
// ====================== PROFESSIONAL SUMMARY CARD ======================
const SummaryCard = ({ title, value, icon, color }) => (
  <div className="col-lg-3 col-md-6 mb-4">
    <div className="card h-100 shadow-sm border-0 rounded-3 overflow-hidden">
      <div className="card-body p-4">
        <div className=" align-items-center gap-3 mb-3">
          <div 
            className="rounded-3 p-3" 
            style={{ backgroundColor: color + '20', color: color }}
          >
            <FontAwesomeIcon icon={icon} size="lg" />
          </div>
          <div>
            <h6 className="text-muted fw-semibold mb-1">{title}</h6>
            <h3 className="fw-bold mb-0 text-dark">{value}</h3>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AllServicesReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: today,
    end_date: today,
    search: "",
    page: 1,
    per_page: 500
  });

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const perPageOptions = [500, 1000, 2000];

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.search.trim()) params.append("search", filters.search.trim());
      params.append("page", filters.page);
      params.append("per_page", filters.per_page);

      const res = await axios.get(`${config.smsUrl}/creport/all_services_report_v2`, { params });
      setReport(res.data);
    } catch (err) {
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      console.error(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1 }));
      fetchReport();
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [filters.start_date, filters.end_date, filters.search]);

  useEffect(() => {
    fetchReport();
  }, [filters.page, filters.per_page]);


  const handleDownload = async (format) => {
    try {
      const response = await axios.get(
        `${config.smsUrl}/creport/all_services_report_v2_downloads`,
        {
          params: { ...filters, format },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          format === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `All_Services_Report.${format === "pdf" ? "pdf" : "xlsx"}`
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const { summary = {}, data = [], pagination = {} } = report || {};

  const formatSSP = (amount) => `SSP ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
  const formatUSD = (amount) => `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getVisitLink = (row) => {
    if (row.payment_type === "INSURANCE") {
      return `/invoice-details/${row.visit_id}/${row.provider_id || 0}/${row.claim_status || 0}/${row.provider_type || 'private'}`;
    }
    if (row.visit_type === '1') {
      return `/billingreceipt/${row.visit_id}`;
    }
    if (row.visit_type === '2') {
      const admissionId = row.admission_id && row.admission_id.trim() ? row.admission_id : row.visit_id;
      return `/viewinpatientbill/${row.visit_id}/${admissionId}/1`;
    }
    return `/billingreceipt/${row.visit_id}`;
  };

  const handleVisitClick = (row) => {
    const link = getVisitLink(row);
    window.open(link, "_blank");
  };

return (
    <>
      <PageTitle title="All Services Report" />
      <SubHeader title="Reports" subTitle="Service Revenue • All Transactions">
        <FontAwesomeIcon icon={faStethoscope} />
      </SubHeader>

      <Container className="mt-3">
        <MainSnackbar />

        {/* FILTERS - Professional Card */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body py-3">
            <div className="row align-items-end g-3">
              <div className="col-auto">
                <BackButton to="/reports/generalreports" text="Reports" />
              </div>
              <div className="col-auto">
                <label className="form-label fw-bold small text-muted mb-1">Start Date</label>
                <input 
                  type="date" 
                  className="form-control form-control-sm" 
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} 
                />
              </div>
              <div className="col-auto">
                <label className="form-label fw-bold small text-muted mb-1">End Date</label>
                <input 
                  type="date" 
                  className="form-control form-control-sm" 
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} 
                />
              </div>
              <div className="col">
                <label className="form-label fw-bold small text-muted mb-1">Search</label>
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search Service, Patient, Visit ID..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>

              <div className="col-auto">
  <div className="flex-column">
    <label className="form-label small text-muted fw-semibold mb-1">
      Rows per page
    </label>

    <div className="input-group input-group-sm shadow-sm">
      <span
        className="input-group-text bg-white border-end-0 text-muted"
        style={{ fontSize: "0.85rem" }}
      >
        <FontAwesomeIcon icon={faList} />
      </span>

      <select
        className="form-select border-start-0"
        value={filters.per_page}
        onChange={(e) =>
          setFilters({
            ...filters,
            per_page: parseInt(e.target.value),
            page: 1,
          })
        }
        style={{
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        {perPageOptions.map((n) => (
          <option key={n} value={n}>
            {n} rows
          </option>
        ))}
      </select>
    </div>
  </div>
</div>
              <div className="col-md-3 text-end">
                <button className="btn btn-success btn-sm px-3" onClick={() => handleDownload("excel")}>
                  <FontAwesomeIcon icon={faFileExcel} /> Excel
                </button>
                <button className="btn btn-danger btn-sm px-3" onClick={() => handleDownload("pdf")}>
                  <FontAwesomeIcon icon={faFilePdf} /> PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS - Clean & Professional */}
        {/* SUMMARY CARDS */}
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-4 g-3 mb-3">
          <SummaryCard 
            title="Total Transactions" 
            value={summary.total_transactions || 0} 
            icon={faCalendarAlt} 
            color="#0d6efd" 
          />
          <SummaryCard 
            title="Revenue (SSP)" 
            value={formatSSP(summary.total_revenue_ssp)} 
            icon={faCoins} 
            color="#198754" 
          />
          <SummaryCard 
            title="Revenue (USD)" 
            value={formatUSD(summary.total_revenue_usd)} 
            icon={faDollarSign} 
            color="#0dcaf0" 
          />
          <SummaryCard 
            title="Payment Split" 
            value={
              <div>
                <div><strong>{summary.cash_count || 0}</strong> Cash</div>
                <div><strong>{summary.insurance_count || 0}</strong> Insurance</div>
              </div>
            } 
            icon={faCreditCard} 
            color="#6f42c1" 
          />
        </div>

        {/* TABLE SECTION */}
        {loading ? (
          <div className="text-center py-5"><LoadingGif /></div>
        ) : data.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <h5>No services found for the selected period</h5>
            <small>Try adjusting your filters</small>
          </div>
        ) : (
          <>
            <div className="card shadow-sm border-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle table-sm mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Visit ID</th>
                      <th>Patient</th>
                      <th>Service</th>
                      <th>Department</th>
                      <th className="text-center">Qty</th>
                      <th className="text-end">SSP</th>
                      <th className="text-end">USD</th>
                      <th>Payment</th>
                      <th>Insurer</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, i) => (
                      <tr key={i} onClick={() => handleVisitClick(item)} style={{ cursor: "pointer" }}>
                        <td><strong>{(filters.page - 1) * filters.per_page + i + 1}</strong></td>
                        <td>{new Date(item.sale_date).toLocaleDateString('en-GB')}</td>
                        <td><span className="badge bg-primary fs-6">{item.visit_id}</span></td>
                        <td className="fw-semibold text-info">{item.patient_name}</td>
                        <td><strong>{item.service_name}</strong></td>
                        <td className="text-muted">{item.department_name}</td>
                        <td className="text-center fw-semibold">{item.quantity}</td>
                        <td className="text-end fw-bold text-primary">{formatSSP(item.revenue_ssp)}</td>
                        <td className="text-end fw-bold text-success">
                          {item.revenue_usd > 0 ? formatUSD(item.revenue_usd) : "-"}
                        </td>
                        <td>
                          {item.payment_type === 'CASH' 
                            ? <span className="badge bg-success">CASH</span>
                            : <span className="badge bg-info">INSURANCE</span>
                          }
                        </td>
                        <td className="small">{item.insurance_provider || '-'}</td>
                        <td>
                          {item.patient_type === 'Inpatient' 
                            ? <FontAwesomeIcon icon={faHospital} className="text-danger" />
                            : <FontAwesomeIcon icon={faUserMd} className="text-success" />
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4 px-2">
                <div className="text-muted small">
                  Showing {(filters.page - 1) * filters.per_page + 1} to{" "}
                  {Math.min(filters.page * filters.per_page, pagination.total_records)} of {pagination.total_records} records
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary btn-sm" disabled={filters.page === 1}
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Prev
                  </button>
                  <button className="btn btn-primary btn-sm disabled px-4">
                    Page {filters.page} of {pagination.total_pages}
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" disabled={filters.page === pagination.total_pages}
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}>
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="text-center mt-5 text-muted small">
          Generated: {report?.generated_at || "-"} | Period: {filters.start_date} — {filters.end_date}
        </div>
      </Container>
    </>
  );
};
const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(null, mapDispatchToProps)(AllServicesReport);
